import React, { useState, useEffect } from 'react';
import dataStore, { getProtocolSuggestions } from '../utils/dataStore';
import useProtocolList from '../hooks/useProtocolList';
import ApplyAllCasesModal from './ApplyAllCasesModal';
import './StainProtocolMapping.css';

const StainProtocolMapping = () => {
    const [dataStatus, setDataStatus] = useState(dataStore.getStatus());
    const stainProtocols = useProtocolList('stain');
    const availableProtocols = stainProtocols;
    const [cases, setCases] = useState([]);
    const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [showApplyAllModal, setShowApplyAllModal] = useState(false);

    // Subscribe to data store changes
    useEffect(() => {
        const unsubscribe = dataStore.subscribe(() => {
            setDataStatus(dataStore.getStatus());
        });
        return unsubscribe;
    }, []);

    // Generate cases with stain slides when data changes
    useEffect(() => {
        if (dataStatus.processedData && dataStatus.processedData.length > 0) {
            console.log('🔄 StainProtocolMapping: Regenerating cases due to data change');
            console.log('🔍 Current case ID mappings:', dataStatus.caseIdMappings);
            console.log('🔍 Number of case ID mappings:', Object.keys(dataStatus.caseIdMappings || {}).length);

            const stainCases = dataStore.generateStainProtocolCases();
            console.log(`📊 Generated ${stainCases.length} stain cases`);

            // Only reset case index if the number of cases changed significantly
            // (e.g., new cases added or cases removed), not just when data is updated
            const previousCaseCount = cases.length;
            const newCaseCount = stainCases.length;

            setCases(stainCases);

            // Only reset to first case if:
            // 1. This is the initial load (no previous cases)
            // 2. The number of cases changed significantly (more than just protocol updates)
            // 3. Current case index is out of bounds
            if (previousCaseCount === 0 ||
                Math.abs(newCaseCount - previousCaseCount) > 0 ||
                currentCaseIndex >= newCaseCount) {
                setCurrentCaseIndex(0);
            }
            // Otherwise, keep the current case index to maintain user's position
        }
    }, [dataStatus]);

    // Get all slides with stain types for the case (regardless of mapping status)
    const getAllStainSlides = (caseData) => {
        if (!caseData || !caseData.slides) return [];
        return caseData.slides.filter(slide =>
            slide.localStainID // Show all slides that have a stain type
        );
    };

    // Group slides by stain type
    const groupSlidesByStainType = (slides) => {
        const groups = {};
        slides.forEach(slide => {
            const stainType = slide.localStainID;
            if (!groups[stainType]) {
                groups[stainType] = [];
            }
            groups[stainType].push(slide);
        });
        return groups;
    };

    // Navigation functions
    const handlePreviousCase = () => {
        const newIndex = currentCaseIndex > 0 ? currentCaseIndex - 1 : cases.length - 1;
        setCurrentCaseIndex(newIndex);
    };

    const handleNextCase = () => {
        const newIndex = currentCaseIndex < cases.length - 1 ? currentCaseIndex + 1 : 0;
        setCurrentCaseIndex(newIndex);
    };

    // Skip to next case with unmapped stain protocols
    const handleSkipToNextUnmapped = () => {
        // Force a refresh of the cases data to get the latest state
        const freshCases = dataStore.generateStainProtocolCases();
        let foundIndex = -1;

        // Search forward from current position
        for (let i = 0; i < freshCases.length; i++) {
            const caseIndex = (currentCaseIndex + i) % freshCases.length;
            const caseData = freshCases[caseIndex];

            if (caseData && caseData.slides) {
                const allStainSlides = getAllStainSlides(caseData);
                const stainGroups = groupSlidesByStainType(allStainSlides);

                // Check if any stain group has unmapped slides
                const hasUnmapped = Object.entries(stainGroups).some(([stainType, slides]) => {
                    const unmappedCount = slides.filter(s => !s.stainProtocols || s.stainProtocols.length === 0).length;
                    return unmappedCount > 0;
                });

                if (hasUnmapped) {
                    foundIndex = caseIndex;
                    break;
                }
            }
        }

        if (foundIndex !== -1) {
            setCurrentCaseIndex(foundIndex);
            console.log(`⏭️ Skipped to case ${freshCases[foundIndex].bdsaId} with unmapped stain protocols`);
        } else {
            console.log('🎉 No more cases with unmapped stain protocols found!');
            alert('🎉 Great job! No more cases with unmapped stain protocols found.');
        }
    };

    // Toggle group expansion
    const toggleGroupExpansion = (stainType) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(stainType)) {
            newExpanded.delete(stainType);
        } else {
            newExpanded.add(stainType);
        }
        setExpandedGroups(newExpanded);
    };

    const handleApplyStainProtocol = (slides, protocolId, targetCase = null) => {
        console.log('Applying stain protocol', protocolId, 'to slides:', slides);

        // Use the provided target case, or fall back to current case
        const currentCase = targetCase || cases[currentCaseIndex];
        if (!currentCase) return;

        // Convert GUID to name for storage (data stores names, not GUIDs)
        const protocol = availableProtocols.find(p => p.id === protocolId);
        const protocolName = protocol ? protocol.name : protocolId;

        // Apply protocol to each slide using the NAME
        slides.forEach(slide => {
            dataStore.addProtocolMapping(currentCase.bdsaId, slide.id, protocolName, 'stain');
        });

        console.log(`✅ Applied protocol ${protocolName} (${protocolId}) to ${slides.length} slides`);

        // Force a re-render by updating the data status
        setDataStatus(dataStore.getStatus());
    };

    const handleRemoveStainProtocol = (slides, protocolToRemove) => {
        // Get the current case
        const currentCase = cases[currentCaseIndex];
        if (!currentCase) {
            console.error('❌ No current case found');
            return;
        }

        // Remove protocol from each slide
        slides.forEach(slide => {
            dataStore.removeProtocolMapping(currentCase.bdsaId, slide.id, protocolToRemove, 'stain');
        });

        console.log(`✅ Removed protocol ${protocolToRemove} from ${slides.length} slides`);

        // Force a re-render by updating the data status
        setDataStatus(dataStore.getStatus());
    };

    // Get suggestion for a specific stain type
    const getSuggestionForStainType = (stainType) => {
        return getProtocolSuggestions(stainType, 'stain');
    };

    // Check if a protocol is suggested for a stain type
    const isProtocolSuggested = (protocolName, stainType) => {
        const suggestion = getSuggestionForStainType(stainType);
        return suggestion.suggested === protocolName;
    };

    // Get suggestion confidence for a protocol
    const getSuggestionConfidence = (protocolName, stainType) => {
        const suggestion = getSuggestionForStainType(stainType);
        return suggestion.suggested === protocolName ? suggestion.confidence : 0;
    };

    // Helper function to get protocol name from ID
    const getProtocolName = (protocolId) => {
        const protocol = availableProtocols.find(p => p.id === protocolId);
        return protocol ? protocol.name : protocolId; // Fallback to ID if not found
    };

    // Helper function to get full protocol info for tooltips
    const getProtocolInfo = (protocolId) => {
        const protocol = availableProtocols.find(p => p.id === protocolId);
        if (protocol) {
            return {
                name: protocol.name,
                id: protocol.id,
                description: protocol.description || '',
                abbreviation: protocol.abbreviation || ''
            };
        }
        return {
            name: protocolId,
            id: protocolId,
            description: '',
            abbreviation: ''
        };
    };

    // Helper function to create tooltip text
    const getProtocolTooltip = (protocolId) => {
        const info = getProtocolInfo(protocolId);
        const parts = [];

        // Add full name
        parts.push(`Name: ${info.name}`);

        // Add GUID
        parts.push(`GUID: ${info.id}`);

        // Add abbreviation if different from name
        if (info.abbreviation && info.abbreviation !== info.name) {
            parts.push(`Abbreviation: ${info.abbreviation}`);
        }

        // Add description if available
        if (info.description) {
            parts.push(`Description: ${info.description}`);
        }

        return parts.join('\n');
    };

    // Auto-apply suggestions for the current case
    const handleAutoApplySuggestions = () => {
        const currentCase = cases[currentCaseIndex];
        if (!currentCase) return;

        const allStainSlides = getAllStainSlides(currentCase);
        const stainGroups = groupSlidesByStainType(allStainSlides);

        let appliedCount = 0;
        let skippedCount = 0;
        const skippedTypes = [];

        Object.entries(stainGroups).forEach(([stainType, slides]) => {
            const suggestion = getSuggestionForStainType(stainType);

            // Only apply if we have high confidence (>= 80%), it's an exact match, and it's not IGNORE
            if (suggestion.suggested && suggestion.confidence >= 0.8 && suggestion.isExactMatch &&
                suggestion.suggested.toUpperCase() !== 'IGNORE') {
                // Check if this protocol is already fully applied to all slides
                const alreadyApplied = slides.every(slide =>
                    slide.bdsaStainProtocol && slide.bdsaStainProtocol.includes(suggestion.suggested)
                );

                if (!alreadyApplied) {
                    handleApplyStainProtocol(slides, suggestion.suggested);
                    appliedCount++;
                }
            } else {
                skippedCount++;
                skippedTypes.push({
                    type: stainType,
                    reason: suggestion.suggested
                        ? `Low confidence (${Math.round(suggestion.confidence * 100)}%)`
                        : 'No suggestion available'
                });
            }
        });

        // Show results with better feedback - only log summary
        if (appliedCount > 0) {
            console.log(`✅ Auto-applied ${appliedCount} stain protocol suggestion(s)`);
        } else if (skippedCount > 0) {
            console.log(`⚠️ No stain suggestions applied (${skippedCount} skipped due to low confidence)`);
        }
    };

    // Auto-apply suggestions for ALL cases with progress callback
    const handleAutoApplyAllCases = async (progressCallback) => {
        console.log(`🚀 Starting auto-apply suggestions for ${cases.length} cases`);

        let totalProcessed = 0;
        let totalApplied = 0;
        let totalSkipped = 0;

        for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
            const caseData = cases[caseIndex];

            // Update progress
            const progress = {
                current: caseIndex + 1,
                total: cases.length,
                percentage: ((caseIndex + 1) / cases.length) * 100,
                applied: totalApplied,
                skipped: totalSkipped,
                currentCase: caseData.bdsaId
            };
            progressCallback(progress);

            // Add a small delay to make progress visible (only if there are many cases)
            if (cases.length > 10 && caseIndex < cases.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Use the caseData directly instead of relying on state updates
            if (caseData) {
                const allStainSlides = getAllStainSlides(caseData);
                const stainGroups = groupSlidesByStainType(allStainSlides);

                let caseApplied = 0;
                let caseSkipped = 0;

                Object.entries(stainGroups).forEach(([stainType, slides]) => {
                    const suggestion = getSuggestionForStainType(stainType);

                    // Debug: Log suggestion details for first few stain types
                    if (totalProcessed === 0 && Object.keys(stainGroups).indexOf(stainType) < 3) {
                        console.log(`🔍 DEBUG suggestion for stain type "${stainType}":`, {
                            suggested: suggestion.suggested,
                            confidence: suggestion.confidence,
                            isExactMatch: suggestion.isExactMatch,
                            reason: suggestion.reason,
                            slidesCount: slides.length
                        });
                    }

                    // Use the same logic as the single-case auto-apply
                    if (suggestion.suggested && suggestion.confidence >= 0.8 && suggestion.isExactMatch &&
                        suggestion.suggested.toUpperCase() !== 'IGNORE') {
                        // Check if this protocol is already fully applied to all slides
                        const alreadyApplied = slides.every(slide =>
                            slide.bdsaStainProtocol && slide.bdsaStainProtocol.includes(suggestion.suggested)
                        );

                        if (!alreadyApplied) {
                            handleApplyStainProtocol(slides, suggestion.suggested, caseData);
                            caseApplied++;
                        }
                    } else {
                        caseSkipped++;
                    }
                });

                totalApplied += caseApplied;
                totalSkipped += caseSkipped;
                totalProcessed++;
            }
        }

        // Final progress update
        const finalProgress = {
            current: cases.length,
            total: cases.length,
            percentage: 100,
            applied: totalApplied,
            skipped: totalSkipped,
            currentCase: null
        };
        progressCallback(finalProgress);

        console.log(`\n🎯 Auto-apply ALL cases complete:`);
        console.log(`  📊 Total cases processed: ${totalProcessed}`);
        console.log(`  📊 Total suggestions applied: ${totalApplied}`);
        console.log(`  📊 Total suggestions skipped: ${totalSkipped}`);

        return {
            processed: totalProcessed,
            applied: totalApplied,
            skipped: totalSkipped
        };
    };

    if (cases.length === 0) {
        return (
            <div className="stain-protocol-mapping">
                <div className="header">
                    <h2>Stain Protocol Mapping</h2>
                    <p>No cases with stain slides found.</p>
                </div>
                <div className="no-cases">
                    <p>Make sure you have:</p>
                    <ul>
                        <li>Loaded data with BDSA Case IDs assigned</li>
                        <li>Local Stain IDs in your data</li>
                    </ul>
                </div>
            </div>
        );
    }

    const currentCase = cases[currentCaseIndex];
    const allStainSlides = getAllStainSlides(currentCase);
    const stainGroups = groupSlidesByStainType(allStainSlides);

    // Calculate unmapped slides for current case
    const getUnmappedCounts = (caseData) => {
        if (!caseData || !caseData.slides) return { stain: 0, region: 0, total: 0 };

        let unmappedStain = 0;
        let unmappedRegion = 0;

        caseData.slides.forEach(slide => {
            // Check stain protocol mapping
            if (slide.localStainID && (!slide.bdsaStainProtocol || slide.bdsaStainProtocol.length === 0)) {
                unmappedStain++;
            }
            // Check region protocol mapping  
            if (slide.localRegionId && (!slide.bdsaRegionProtocol || slide.bdsaRegionProtocol.length === 0)) {
                unmappedRegion++;
            }
        });

        return {
            stain: unmappedStain,
            region: unmappedRegion,
            total: unmappedStain + unmappedRegion
        };
    };

    const unmappedCounts = getUnmappedCounts(currentCase);

    // Debug logging (commented out to reduce console noise)
    // console.log('🔍 DEBUG - Current case:', {
    //     bdsaId: currentCase.bdsaId,
    //     localCaseId: currentCase.localCaseId,
    //     totalSlides: currentCase.slides.length,
    //     stainSlides: allStainSlides.length,
    //     allSlides: currentCase.slides.map(s => ({
    //         id: s.id,
    //         stainType: s.stainType,
    //         status: s.status,
    //         hasStainProtocol: s.hasStainProtocol
    //     }))
    // });

    // console.log('🔍 DEBUG - Stain groups:', stainGroups);

    return (
        <div className="stain-protocol-mapping">
            <div className="header">
                <div className="case-navigation-compact">
                    <button
                        className="nav-btn prev-btn"
                        onClick={handlePreviousCase}
                        disabled={cases.length <= 1}
                    >
                        ←
                    </button>
                    <div className="case-pill">
                        <div className="case-id">{currentCase.bdsaId}</div>
                        <div className="case-details">Local: {currentCase.localCaseId} • {currentCaseIndex + 1} of {cases.length}</div>
                        {currentCase.sectioningProtocolName && (
                            <div className="case-blocking-protocol">
                                Sectioning: {currentCase.sectioningProtocolName}
                            </div>
                        )}
                        {unmappedCounts.total > 0 && (
                            <div className="unmapped-counts">
                                <span className="unmapped-stain">{unmappedCounts.stain} unmapped stain</span>
                                {unmappedCounts.region > 0 && (
                                    <span className="unmapped-region"> • {unmappedCounts.region} unmapped region</span>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        className="nav-btn next-btn"
                        onClick={handleNextCase}
                        disabled={cases.length <= 1}
                    >
                        →
                    </button>
                    <button
                        className="apply-all-cases-btn"
                        onClick={() => setShowApplyAllModal(true)}
                        title="Auto-apply high-confidence protocol suggestions to all stain types across ALL cases"
                    >
                        🚀 Apply to All Cases
                    </button>
                </div>
                <div className="auto-apply-section">
                    <button
                        className="auto-apply-btn"
                        onClick={handleAutoApplySuggestions}
                        title="Auto-apply high-confidence protocol suggestions to all stain types in this case"
                    >
                        ⚡ Auto-Apply Suggestions
                    </button>
                    <button
                        className="skip-unmapped-btn"
                        onClick={handleSkipToNextUnmapped}
                        title="Skip to the next case that has unmapped stain protocols"
                    >
                        ⏭️ Skip to Next Unmapped
                    </button>
                </div>
            </div>

            <div className="mapping-interface">
                <h3>Map Stain Protocols to Slides</h3>
                <p>Select a stain protocol for each group of slides with the same stain type.</p>

                {Object.keys(stainGroups).length === 0 ? (
                    <div className="no-unmapped-slides">
                        <p>No slides with stain types found in this case.</p>
                    </div>
                ) : (
                    <div className="stain-groups">
                        {Object.entries(stainGroups).map(([stainType, slides]) => {
                            const isExpanded = expandedGroups.has(stainType);
                            const mappedCount = slides.filter(s => s.bdsaStainProtocol && s.bdsaStainProtocol.length > 0).length;
                            const unmappedCount = slides.filter(s => !s.bdsaStainProtocol || s.bdsaStainProtocol.length === 0).length;

                            // Find all protocols that are applied to any slide in this group with counts
                            const protocolCounts = {};
                            slides.forEach(slide => {
                                (slide.bdsaStainProtocol || []).forEach(protocol => {
                                    protocolCounts[protocol] = (protocolCounts[protocol] || 0) + 1;
                                });
                            });

                            const allGroupProtocols = Object.keys(protocolCounts);
                            const fullyAppliedProtocols = allGroupProtocols.filter(protocol =>
                                protocolCounts[protocol] === slides.length
                            );

                            // Helper function to convert protocol names to GUIDs for comparison
                            const getProtocolGuid = (protocolName) => {
                                // Try exact match first
                                let protocol = availableProtocols.find(p => p.name === protocolName);

                                // If no exact match, try case-insensitive match
                                if (!protocol) {
                                    protocol = availableProtocols.find(p =>
                                        p.name.toLowerCase() === protocolName.toLowerCase()
                                    );
                                }

                                // If still no match, try partial match (protocol name might be truncated)
                                if (!protocol) {
                                    protocol = availableProtocols.find(p =>
                                        p.name.toLowerCase().startsWith(protocolName.toLowerCase()) ||
                                        protocolName.toLowerCase().startsWith(p.name.toLowerCase())
                                    );
                                }

                                return protocol ? protocol.id : protocolName;
                            };

                            // Convert fully applied protocol names to GUIDs for filtering
                            const fullyAppliedGuids = fullyAppliedProtocols.map(getProtocolGuid);
                            const allGroupGuids = allGroupProtocols.map(getProtocolGuid);

                            console.log(`🔍 Group ${stainType} protocol counts:`, protocolCounts);
                            console.log(`🔍 Fully applied protocols:`, fullyAppliedProtocols);

                            return (
                                <div key={stainType} className="stain-group">
                                    <div
                                        className="group-header clickable"
                                        onClick={() => toggleGroupExpansion(stainType)}
                                    >
                                        <div className="group-title">
                                            <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                            <h4>Stain Type: {stainType}</h4>
                                        </div>
                                        <div className="group-stats">
                                            <p>{slides.length} slide(s)</p>
                                            {mappedCount > 0 && <span className="mapped-count">({mappedCount} mapped)</span>}
                                            {unmappedCount > 0 && <span className="unmapped-count">({unmappedCount} unmapped)</span>}
                                        </div>
                                    </div>

                                    {/* Group-level protocols with counts */}
                                    {allGroupProtocols.length > 0 && (
                                        <div className="group-protocols">
                                            <strong>Group Protocols:</strong>
                                            <div className="protocol-chips">
                                                {allGroupProtocols.map((protocol, idx) => {
                                                    const count = protocolCounts[protocol];
                                                    const isFullyApplied = count === slides.length;
                                                    return (
                                                        <span key={idx} className={`protocol-chip group-chip ${isFullyApplied ? 'fully-applied' : 'partially-applied'}`}>
                                                            <span title={getProtocolTooltip(protocol)}>{getProtocolName(protocol)} ({count}/{slides.length})</span>
                                                            <button
                                                                className="remove-protocol-btn"
                                                                onClick={() => handleRemoveStainProtocol(slides, protocol)}
                                                                title={`Remove ${getProtocolName(protocol)} from all slides`}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Protocol selection - always visible */}
                                    <div className="protocol-selection">
                                        <label>Add Stain Protocol:</label>
                                        <div className="available-protocols">
                                            {availableProtocols
                                                .filter(protocol => !allGroupGuids.includes(protocol.id))
                                                .map(protocol => {
                                                    const isPartiallyApplied = allGroupGuids.includes(protocol.id);
                                                    const currentCount = protocolCounts[protocol.name] || 0;
                                                    const isSuggested = isProtocolSuggested(protocol.name, stainType);
                                                    const confidence = getSuggestionConfidence(protocol.name, stainType);

                                                    return (
                                                        <button
                                                            key={protocol.id}
                                                            className={`add-protocol-btn ${isPartiallyApplied ? 'partially-applied' : ''} ${isSuggested ? 'suggested' : ''}`}
                                                            onClick={() => handleApplyStainProtocol(slides, protocol.id)}
                                                            title={isSuggested
                                                                ? `⭐ SUGGESTED: ${protocol.name} (${Math.round(confidence * 100)}% confidence) - ${getSuggestionForStainType(stainType).reason}`
                                                                : isPartiallyApplied
                                                                    ? `Apply ${protocol.name} to remaining ${slides.length - currentCount} slides`
                                                                    : `Add ${protocol.name} to all slides`
                                                            }
                                                        >
                                                            {isSuggested && '⭐ '}
                                                            + {protocol.name}
                                                            {isPartiallyApplied && ` (${currentCount}/${slides.length})`}
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="slides-table-container">
                                            <table className="slides-table">
                                                <thead>
                                                    <tr>
                                                        <th>File</th>
                                                        <th>Status</th>
                                                        <th>Protocols</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {slides.map(slide => (
                                                        <tr key={slide.id} className={`slide-row ${(slide.bdsaStainProtocol && slide.bdsaStainProtocol.length > 0) ? 'mapped' : 'unmapped'}`}>
                                                            <td className="file-cell">
                                                                <div className="file-name">{slide.filename}</div>
                                                                <div className="slide-id">{slide.id}</div>
                                                            </td>
                                                            <td className="status-cell">
                                                                <span className={`status-badge ${(slide.bdsaStainProtocol && slide.bdsaStainProtocol.length > 0) ? 'mapped' : 'unmapped'}`}>
                                                                    {(slide.bdsaStainProtocol && slide.bdsaStainProtocol.length > 0) ? '✓ Mapped' : '○ Unmapped'}
                                                                </span>
                                                            </td>
                                                            <td className="protocols-cell">
                                                                {slide.bdsaStainProtocol && slide.bdsaStainProtocol.length > 0 ? (
                                                                    <div className="protocol-tags">
                                                                        {slide.bdsaStainProtocol.map((protocol, idx) => (
                                                                            <span key={idx} className="protocol-tag" title={getProtocolTooltip(protocol)}>
                                                                                {getProtocolName(protocol)}
                                                                                <button
                                                                                    className="remove-protocol-btn"
                                                                                    onClick={() => handleRemoveStainProtocol([slide], protocol)}
                                                                                    title={`Remove ${getProtocolName(protocol)}`}
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <span className="no-protocols">No protocols</span>
                                                                )}
                                                            </td>
                                                            <td className="actions-cell">
                                                                <div className="slide-actions">
                                                                    {availableProtocols
                                                                        .filter(protocol => !(slide.bdsaStainProtocol || []).includes(protocol.id))
                                                                        .map(protocol => (
                                                                            <button
                                                                                key={protocol.id}
                                                                                className="apply-protocol-btn"
                                                                                onClick={() => handleApplyStainProtocol([slide], protocol.id)}
                                                                                title={`Apply ${protocol.name}`}
                                                                            >
                                                                                + {protocol.name}
                                                                            </button>
                                                                        ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Apply All Cases Modal */}
            <ApplyAllCasesModal
                isOpen={showApplyAllModal}
                onClose={() => setShowApplyAllModal(false)}
                onApplyAll={handleAutoApplyAllCases}
                protocolType="stain"
                totalCases={cases.length}
            />
        </div>
    );
};

export default StainProtocolMapping;
